import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  useWeddingSettings,
  useUpdateWeddingSettings,
  useBudgetCategories,
  useCreateCategory,
  useDeleteCategory,
  useBudgetExpenses,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
  useAllExpensePayments,
  BudgetCategory,
  BudgetExpense,
} from '@/hooks/useBudget';
import { ExpensePaymentsManager } from '@/components/dashboard/ExpensePayments';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import {
  Wallet,
  Plus,
  Edit2,
  Trash2,
  ArrowLeft,
  Target,
  Receipt,
  CreditCard,
  TrendingUp,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { cn } from '@/lib/utils';

const CATEGORY_COLORS = [
  '#d4a5a5', '#8fbc8f', '#d4af37', '#a5c4d4', '#c4a5d4',
  '#d4c4a5', '#a5d4c4', '#d4a5c4', '#b5c4a5', '#c4b5a5',
];

const DEFAULT_CATEGORIES = [
  { name: 'Catering', color: '#d4a5a5' },
  { name: 'Música y Entretenimiento', color: '#8fbc8f' },
  { name: 'Decoración y Flores', color: '#d4af37' },
  { name: 'Fotografía y Video', color: '#a5c4d4' },
  { name: 'Vestimenta', color: '#c4a5d4' },
  { name: 'Lugar y Venue', color: '#d4c4a5' },
  { name: 'Invitaciones', color: '#a5d4c4' },
  { name: 'Transporte', color: '#d4a5c4' },
  { name: 'Otros', color: '#b5c4a5' },
];

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
  }).format(amount);
};

const Budget = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { data: settings } = useWeddingSettings();
  const { data: categories = [], isLoading: categoriesLoading } = useBudgetCategories();
  const { data: expenses = [], isLoading: expensesLoading } = useBudgetExpenses();

  const updateSettings = useUpdateWeddingSettings();
  const createCategory = useCreateCategory();
  const deleteCategory = useDeleteCategory();
  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();
  const deleteExpense = useDeleteExpense();
  const { data: allPayments = [] } = useAllExpensePayments();

  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<BudgetExpense | null>(null);
  const [totalBudgetInput, setTotalBudgetInput] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  const [expenseForm, setExpenseForm] = useState({
    category_id: '',
    description: '',
    estimated_amount: '',
    amount: '',
    is_paid: false,
  });

  // Calculations
  const stats = useMemo(() => {
    const totalBudget = settings?.total_budget || 0;
    const totalEstimated = expenses.reduce((sum, e) => sum + Number(e.estimated_amount || 0), 0);
    const totalReal = expenses.reduce((sum, e) => sum + Number(e.amount), 0);
    const totalPaid = allPayments.reduce((sum, p) => sum + Number(p.amount), 0);

    return { totalBudget, totalEstimated, totalReal, totalPaid };
  }, [settings, expenses, allPayments]);

  const chartData = useMemo(() => {
    const categoryTotals = new Map<string, { name: string; value: number; color: string }>();

    expenses.forEach((expense) => {
      const cat = categories.find(c => c.id === expense.category_id);
      if (cat) {
        const existing = categoryTotals.get(cat.id);
        if (existing) {
          existing.value += Number(expense.amount);
        } else {
          categoryTotals.set(cat.id, {
            name: cat.name,
            value: Number(expense.amount),
            color: cat.color
          });
        }
      }
    });

    return Array.from(categoryTotals.values()).filter(d => d.value > 0);
  }, [expenses, categories]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    navigate('/auth');
    return null;
  }

  const handleSaveBudget = () => {
    const amount = parseFloat(totalBudgetInput.replace(/,/g, ''));
    if (!isNaN(amount)) {
      updateSettings.mutate({ total_budget: amount });
      setBudgetDialogOpen(false);
      setTotalBudgetInput('');
    }
  };

  const handleSaveExpense = () => {
    const data = {
      category_id: expenseForm.category_id,
      description: expenseForm.description,
      estimated_amount: parseFloat(expenseForm.estimated_amount.replace(/,/g, '')) || 0,
      amount: parseFloat(expenseForm.amount.replace(/,/g, '')) || 0,
      vendor_name: null,
      is_paid: expenseForm.is_paid,
      paid_date: expenseForm.is_paid ? new Date().toISOString().split('T')[0] : null,
    };

    if (editingExpense) {
      updateExpense.mutate({ id: editingExpense.id, ...data });
    } else {
      createExpense.mutate(data);
    }

    setExpenseDialogOpen(false);
    resetExpenseForm();
  };

  const handleCreateCategory = () => {
    if (newCategoryName.trim()) {
      const colorIndex = categories.length % CATEGORY_COLORS.length;
      createCategory.mutate({
        name: newCategoryName.trim(),
        color: CATEGORY_COLORS[colorIndex],
        allocated_amount: 0,
      });
      setNewCategoryName('');
      setCategoryDialogOpen(false);
    }
  };

  const handleCreateDefaultCategories = () => {
    DEFAULT_CATEGORIES.forEach((cat, index) => {
      setTimeout(() => {
        createCategory.mutate({ name: cat.name, color: cat.color, allocated_amount: 0 });
      }, index * 100);
    });
  };

  const openEditExpense = (exp: BudgetExpense) => {
    setEditingExpense(exp);
    setExpenseForm({
      category_id: exp.category_id,
      description: exp.description,
      estimated_amount: (exp.estimated_amount || 0).toString(),
      amount: exp.amount.toString(),
      is_paid: exp.is_paid,
    });
    setExpenseDialogOpen(true);
  };

  const resetExpenseForm = () => {
    setExpenseForm({ category_id: '', description: '', estimated_amount: '', amount: '', is_paid: false });
    setEditingExpense(null);
  };

  const togglePaid = (expense: BudgetExpense) => {
    // Only kept for backwards compatibility with older items or marking something without payments yet.
    // Ideally user relies on ExpensePaymentsManager.
    updateExpense.mutate({
      id: expense.id,
      is_paid: !expense.is_paid,
      paid_date: !expense.is_paid ? new Date().toISOString().split('T')[0] : null,
    });
  };

  const getCategoryName = (categoryId: string) => {
    return categories.find(c => c.id === categoryId)?.name || 'Sin categoría';
  };

  const getCategoryColor = (categoryId: string) => {
    return categories.find(c => c.id === categoryId)?.color || '#ccc';
  };

  return (
    <div className="min-h-screen flex bg-background">
      <DashboardSidebar />

      <div className="flex-1 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-serif text-2xl md:text-3xl font-medium text-foreground">
                  Gestión de Presupuesto
                </h1>
                <p className="text-muted-foreground text-sm">
                  Controla todos los gastos de tu boda
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Categoría
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nueva Categoría</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Nombre de categoría</Label>
                      <Input
                        placeholder="ej: Luna de Miel"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                      />
                    </div>
                    <Button onClick={handleCreateCategory} className="w-full" disabled={!newCategoryName.trim()}>
                      Crear Categoría
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={expenseDialogOpen} onOpenChange={(open) => { setExpenseDialogOpen(open); if (!open) resetExpenseForm(); }}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Gasto
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingExpense ? 'Editar' : 'Nuevo'} Gasto</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Categoría</Label>
                      <Select
                        value={expenseForm.category_id}
                        onValueChange={(v) => setExpenseForm({ ...expenseForm, category_id: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Concepto</Label>
                      <Input
                        placeholder="ej: Anticipo catering"
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Costo Estimado</Label>
                        <Input
                          type="text"
                          placeholder="15,000"
                          value={expenseForm.estimated_amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, estimated_amount: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Costo Real</Label>
                        <Input
                          type="text"
                          placeholder="18,000"
                          value={expenseForm.amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="is_paid_form"
                        checked={expenseForm.is_paid}
                        onCheckedChange={(c) => setExpenseForm({ ...expenseForm, is_paid: c === true })}
                      />
                      <Label htmlFor="is_paid_form">Ya está pagado</Label>
                    </div>
                    <Button
                      onClick={handleSaveExpense}
                      className="w-full"
                      disabled={!expenseForm.category_id || !expenseForm.description}
                    >
                      {editingExpense ? 'Actualizar' : 'Guardar'} Gasto
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Top Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="bg-gradient-to-br from-blush-light/30 to-card border-primary/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Target className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Presupuesto Estimado</p>
                    <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
                      <DialogTrigger asChild>
                        <button className="font-serif text-2xl md:text-3xl font-medium text-foreground hover:text-primary transition-colors text-left">
                          {formatCurrency(stats.totalBudget)}
                        </button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Definir Presupuesto Total</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <div>
                            <Label>Monto total estimado</Label>
                            <Input
                              type="text"
                              placeholder="250,000"
                              value={totalBudgetInput}
                              onChange={(e) => setTotalBudgetInput(e.target.value)}
                            />
                          </div>
                          <Button onClick={handleSaveBudget} className="w-full">
                            Guardar
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Click para editar
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Costo Final</p>
                    <p className="font-serif text-2xl md:text-3xl font-medium text-foreground">
                      {formatCurrency(stats.totalReal)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  {stats.totalReal > stats.totalBudget ? (
                    <span className="text-destructive flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Excedido por {formatCurrency(stats.totalReal - stats.totalBudget)}
                    </span>
                  ) : (
                    <span className="text-secondary flex items-center gap-1">
                      Bajo presupuesto por {formatCurrency(stats.totalBudget - stats.totalReal)}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pagado hasta ahora</p>
                    <p className="font-serif text-2xl md:text-3xl font-medium text-secondary">
                      {formatCurrency(stats.totalPaid)}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Pendiente: {formatCurrency(stats.totalReal - stats.totalPaid)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Content Grid */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Chart */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base font-medium">Distribución por Categoría</CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Sin gastos registrados</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: '12px' }}
                        formatter={(value) => <span className="text-muted-foreground">{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Expenses Table */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-medium">Tabla de Gastos</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {categories.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <Wallet className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground mb-4">Primero crea categorías para organizar tus gastos</p>
                    <Button onClick={handleCreateDefaultCategories}>
                      Crear categorías predeterminadas
                    </Button>
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <Receipt className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground mb-4">No hay gastos registrados</p>
                    <Button onClick={() => setExpenseDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar primer gasto
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Categoría</TableHead>
                          <TableHead>Concepto</TableHead>
                          <TableHead className="text-right">Estimado</TableHead>
                          <TableHead className="text-right">Costo Real</TableHead>
                          <TableHead className="text-center">Pagado</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expenses.map((expense) => {
                          const isOverBudget = Number(expense.amount) > Number(expense.estimated_amount || 0) && Number(expense.estimated_amount) > 0;
                          const expensePaid = allPayments.filter(p => p.expense_id === expense.id).reduce((sum, p) => sum + Number(p.amount), 0) + (expense.is_paid ? Number(expense.amount) : 0);
                          const debt = Number(expense.amount) - expensePaid;

                          return (
                            <TableRow key={expense.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: getCategoryColor(expense.category_id) }}
                                  />
                                  <span className="text-sm">{getCategoryName(expense.category_id)}</span>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{expense.description}</TableCell>
                              <TableCell className="text-right text-muted-foreground">
                                {formatCurrency(Number(expense.estimated_amount || 0))}
                              </TableCell>
                              <TableCell className={cn(
                                "text-right font-medium",
                                isOverBudget && "text-destructive"
                              )}>
                                {formatCurrency(Number(expense.amount))}
                              </TableCell>
                              <TableCell className="text-center font-medium">
                                <div className="flex flex-col">
                                  <span>{formatCurrency(expensePaid)}</span>
                                  <span className={`text-xs ${debt > 0 ? 'text-destructive' : 'text-green-600'}`}>
                                    {debt > 0 ? `Resta ${formatCurrency(debt)}` : 'Pagado'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <ExpensePaymentsManager expense={expense} />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => openEditExpense(expense)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Esta acción no se puede deshacer.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => deleteExpense.mutate(expense.id)}>
                                          Eliminar
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                      <TableFooter>
                        <TableRow className="bg-muted/50 font-medium">
                          <TableCell colSpan={2}>TOTALES</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(expenses.reduce((sum, e) => sum + Number(e.estimated_amount || 0), 0))}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(stats.totalReal)}
                          </TableCell>
                          <TableCell className="text-center">
                            {formatCurrency(stats.totalPaid)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Budget;
